import {useMemo} from 'react';

import {useProjectParams} from '../../../pages/ProjectPage/useProjectParams';
import {useRampFlagUseBackendSmoothing} from '../../../util/rampFeatureFlags';
import {DEFAULT_SMOOTHING_SETTINGS} from '../../WorkspaceDrawer/Settings/runLinePlots/linePlotDefaults';
import {getSmoothingTypeDisplayName} from '../../WorkspaceDrawer/Settings/runLinePlots/LinePlotSmoothing';
import {createNumberDisplayFn} from '../displayValueFactory';
import {LinePlotDisplayFnArgs} from '../types';

/**
 * Hook that returns the display row for smoothingType setting.
 * Uses the backend smoothing feature flag to determine display name.
 * This is a hook (not using factory) because it needs to call useRampFlagUseBackendSmoothing.
 *
 * @example
 * const smoothingTypeRow = useSmoothingTypeDisplayRow({
 *   currTemplateLinePlotSettings,
 *   currWorkspaceLinePlotSettings,
 * });
 */
export const useSmoothingTypeDisplayRow = ({
  currTemplateLinePlotSettings,
  currWorkspaceLinePlotSettings,
}: Omit<LinePlotDisplayFnArgs, 'isBackendSmoothingEnabled'>) => {
  const {entityName} = useProjectParams();
  const isBackendSmoothingEnabled = useRampFlagUseBackendSmoothing(entityName);

  return useMemo(() => {
    const currValue = currTemplateLinePlotSettings?.smoothingType;
    const newValue =
      currWorkspaceLinePlotSettings?.smoothingType ??
      DEFAULT_SMOOTHING_SETTINGS.smoothingType;

    return {
      groupTitle: 'Smoothing',
      label: 'Type',
      field: 'smoothingType' as const,
      currValue,
      currDisplayValue:
        currValue == null
          ? undefined
          : getSmoothingTypeDisplayName(currValue, isBackendSmoothingEnabled),
      newValue,
      newDisplayValue: getSmoothingTypeDisplayName(
        newValue,
        isBackendSmoothingEnabled
      ),
    };
  }, [
    currTemplateLinePlotSettings,
    currWorkspaceLinePlotSettings,
    isBackendSmoothingEnabled,
  ]);
};

export const getSmoothingWeightDisplayRow = createNumberDisplayFn({
  field: 'smoothingWeight',
  label: 'Weight',
  groupTitle: 'Smoothing',
  defaultValue: DEFAULT_SMOOTHING_SETTINGS.smoothingWeight,
});
